import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, lighten } from '@material-ui/core/styles';
import { Card, Divider } from '@material-ui/core';
import { get, find } from 'lodash';
import { useTranslation } from '../../../src/i18n';
import EditCardButtons from '../../../components/EditComponents/EditCardButtons';
import PerformanceDate, { performanceStatuses } from '../../../components/PerformanceCardHeader/PerformanceDate';
import TitledBlock from '../../../components/TitledBlock';
import { MEDIA_TAGS_TYPES_IDS, TP, VIDEO_ATTRIBUTE_TYPES_IDS } from '../../../constants/consts';
import { colors } from '../../../constants/styleConstants';
import { getAttributeName, getTagsByType, getTimezonesOptions } from '../../../utils/common';
import DigitalPresenceLiveEventInfo from './DigitalPresenceLiveEventInfo';
import ImageSet from '../../../components/ImageSet';
import OuterLink from '../../../components/OuterLink';
import CostTypeChip from './CostTypeChip';
import DisablingCover from '../../../components/DisablingCover';

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    marginBottom: theme.spacing(2),
    backgroundColor: lighten(colors.darkBlue, 0.95),
    alignItems: 'center',
    position: 'relative',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  dateBox: {
    alignSelf: 'flex-start',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  dateCard: {
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  cardContent: {
    flex: 1,
    padding: theme.spacing(1),
  },
  cardInfo: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  poster: {
    maxWidth: 100,
    marginRight: theme.spacing(1),
  },
  chip: {
    [theme.breakpoints.down('xs')]: {
      top: 114,
    },
  },
  buttonsBlock: {
    marginTop: 20,
    marginBottom: 10,
  },
}));

const recordTitles = record => [
  { title: `${TP}.FN_TIME`, text: getAttributeName(record, VIDEO_ATTRIBUTE_TYPES_IDS.START_TIME) },
  {
    title: `${TP}.REGISTRATION_TIME_ZONE`,
    text: find(getTimezonesOptions(), { value: getAttributeName(record, VIDEO_ATTRIBUTE_TYPES_IDS.TIMEZONE) })?.label,
  },
];

const LivestreamEditCard = ({ livestream, onEdit, onDelete, loading }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const livePerformanceDateInfo = get(getTagsByType(livestream?.tags, MEDIA_TAGS_TYPES_IDS.DATE), '[0].productiondate');
  const costType = getAttributeName(livestream, VIDEO_ATTRIBUTE_TYPES_IDS.COST_TYPE);

  return (
    <Card className={classes.card}>
      {costType && <CostTypeChip label={costType} className={classes.chip} />}
      <div className={classes.dateBox}>
        <PerformanceDate
          date={getAttributeName(livestream, VIDEO_ATTRIBUTE_TYPES_IDS.START_DATE)}
          status={performanceStatuses.livestream}
          className={classes.dateCard}
        />
      </div>
      <div className={classes.cardContent}>
        <OuterLink href={get(livestream, 'url')}>{t(`${TP}.FN_VIEW_URL`)}</OuterLink>
        <div className={classes.cardInfo}>
          {livestream?.thumbnail && <ImageSet images={livestream?.thumbnail?.urls} className={classes.poster} />}
          {recordTitles(livestream)?.map(
            (info, index) =>
              info?.text && (
                <TitledBlock title={t(info?.title)} key={index}>
                  {info?.text}
                </TitledBlock>
              ),
          )}
        </div>
        {livePerformanceDateInfo && (
          <>
            <Divider />
            <DigitalPresenceLiveEventInfo liveEvent={livePerformanceDateInfo} />
          </>
        )}
      </div>
      <EditCardButtons editClick={onEdit} onDelete={onDelete} className={classes.buttonsBlock} />
      {loading && <DisablingCover />}
    </Card>
  );
};

LivestreamEditCard.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

LivestreamEditCard.defaults = {
  loading: false,
};

export default LivestreamEditCard;
